'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import { TWork, type TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import { useChaptersQuery } from '@/features/fanfic-page/hooks';
import { VirtualizedChapterList } from './virtualized-chapter-list';
import { Skeleton } from '../ui/skeleton';

const CHAPTERS_TAB_PAGE_LIMIT = 10;

interface ChaptersTabProps {
  fanfic: TWork;
  selectedChapterId: string | null;
  onOpenFullscreen?: (chapterId: string) => void;
  progress: { chapterIndex: number; progress: number }[];
  updateReadingProgress: (id: string, chapterIndex: number, progress: number) => void;
  /** SSR/prerendered chapters so TOC links exist without JavaScript. */
  initialChapters?: TChapterWithoutContent[];
}

export const ChaptersTab = React.memo(
  ({
    fanfic,
    selectedChapterId,
    onOpenFullscreen,
    progress,
    updateReadingProgress,
    initialChapters = [],
  }: ChaptersTabProps) => {
    const [showWarning, setShowWarning] = useState(false);
    const hasInitialChapters = initialChapters.length > 0;
    const [visibleCount, setVisibleCount] = useState(() =>
      Math.min(CHAPTERS_TAB_PAGE_LIMIT, initialChapters.length || CHAPTERS_TAB_PAGE_LIMIT),
    );

    // Client fallback when SSR did not provide chapters (paginated API fetch).
    const {
      chapters: fetchedChapters,
      isLoading,
      hasMore,
      fetchChapters,
      loadMore,
      loadAll,
      totalDocs,
    } = useChaptersQuery({
      workId: fanfic.id.toString(),
      limit: CHAPTERS_TAB_PAGE_LIMIT,
    });

    useEffect(() => {
      if (hasInitialChapters) {
        return;
      }

      fetchChapters(1);
    }, [fetchChapters, hasInitialChapters]);

    useEffect(() => {
      if (fanfic.isBlocked) {
        setShowWarning(true);
        const timer = setTimeout(() => {
          setShowWarning(false);
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [fanfic.isBlocked]);

    const handleMarkAsRead = (chapterIndex: number) => {
      const calculatedProgress =
        progress.find((item) => item.chapterIndex === chapterIndex)?.progress === 100 ? 0 : 100;
      updateReadingProgress(fanfic.id.toString(), chapterIndex, calculatedProgress);
    };

    const handleRevealMore = useCallback(() => {
      setVisibleCount((previousCount) =>
        Math.min(previousCount + CHAPTERS_TAB_PAGE_LIMIT, initialChapters.length),
      );
    }, [initialChapters.length]);

    const handleRevealAll = useCallback(() => {
      setVisibleCount(initialChapters.length);
    }, [initialChapters.length]);

    const sortedChapters = hasInitialChapters ? initialChapters : fetchedChapters;
    const isInitialLoading = !hasInitialChapters && isLoading && fetchedChapters.length === 0;

    if (isInitialLoading) {
      return (
        <motion.div
          className="grid gap-4 mt-5 md:mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-16 bg-[#F2F3F7]" />
          ))}
        </motion.div>
      );
    }

    return (
      <motion.div
        className="space-y-4 mt-5 md:mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="space-y-4">
          <VirtualizedChapterList
            progress={progress}
            chapters={sortedChapters}
            selectedChapterId={selectedChapterId}
            onMarkAsRead={handleMarkAsRead}
            onOpenFullscreen={onOpenFullscreen}
            hasMore={hasInitialChapters ? false : hasMore}
            onLoadMore={hasInitialChapters ? handleRevealMore : loadMore}
            onLoadAll={hasInitialChapters ? handleRevealAll : loadAll}
            totalDocs={hasInitialChapters ? initialChapters.length : totalDocs}
            isLoadingMore={!hasInitialChapters && isLoading && fetchedChapters.length > 0}
            isRating18={fanfic.rating === '18'}
            workId={fanfic.id.toString()}
            workPathPrefix={fanfic.type === 'original' ? '/originals' : '/fanfics'}
            visibleCount={hasInitialChapters ? visibleCount : undefined}
          />
          {sortedChapters.length === 0 && (
            <div className="text-sm text-[#6e7384] p-3 text-center pb-40">Глав пока нет</div>
          )}
        </div>

        <AnimatePresence>
          {showWarning && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="mt-4 bg-[#F13920]/5 rounded-xl p-3 flex items-start gap-3"
            >
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-red-800 mb-1">Нарушены правила</div>
                <p className="text-sm text-red-700">
                  К сожалению, на текущий момент Вы можете загрузить только произведения Джен и Гет
                  направленностей. Другие направленности противоречат актуальным правилам платформы.
                </p>
              </div>
              <button
                onClick={() => setShowWarning(false)}
                className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  },
);
